// Yes, I know this is copied code but I don't want to create a circular dependency

import type { Enums } from '../types/db';
import type { InstitutionRole, MagicSchoolUser } from '../user';

const DEFAULT_PLAN = 'FREE';

export const subscriptionPlans = {
  [DEFAULT_PLAN]: 1,
  PLUS: 2,
  PARTNER: 3,
} as const;

export const planIdToPlanName = (planId: number) => {
  const keys = Object.keys(subscriptionPlans) as (keyof typeof subscriptionPlans)[];
  return keys.find((key) => subscriptionPlans[key] === planId) ?? DEFAULT_PLAN;
};

export type StripePrice = 'plusMonthly' | 'plusYearly';
// Map our custom plan IDs ("basic", "plus", etc) to Stripe price IDs
export const stripePriceIds: Record<StripePrice, string> = {
  plusMonthly: process.env.NEXT_PUBLIC_STRIPE_PRICE_PLUS_MONTHLY!,
  plusYearly: process.env.NEXT_PUBLIC_STRIPE_PRICE_PLUS_YEARLY!,
};

// Get friendly plan ID ("basic", "plus", etc) by Stripe plan ID
// Used in auth.js to include planId in the user object
export function getFriendlyPlanId(stripePriceId?: string | null): StripePrice | undefined {
  if (!stripePriceId) {
    return undefined;
  }

  return Object.keys(stripePriceIds).find((key) => stripePriceIds[key as StripePrice] === stripePriceId) as StripePrice;
}

const roles: Enums<'user_role'>[] = ['student', 'teacher', 'org_admin', 'internal', 'superuser'] as const;

export const isAtLeastRole = (userRole: Enums<'user_role'> | null, atLeastRole: Enums<'user_role'> | null): boolean => {
  // i think false makes sense?
  if (!userRole || !atLeastRole) return false;
  const userRoleIdx = roles.findIndex((role) => role === userRole);
  const atLeastRoleIdx = roles.findIndex((role) => role === atLeastRole);

  return userRoleIdx >= atLeastRoleIdx;
};

/**
 * Helper function to check if a user has any of the specified institution roles
 */
export const hasAnyInstitutionRole = (user: MagicSchoolUser, requiredRole: InstitutionRole): boolean => {
  return user.institutionRoles.some((userRole) => userRole.role === requiredRole);
};

/**
 * Helper function to get the user's role for a specific institution
 */
export const getUserRoleForInstitution = (user: MagicSchoolUser, institutionId: string): InstitutionRole | undefined => {
  const institutionRole = user.institutionRoles.find((role) => role.institution_id === institutionId);
  return institutionRole?.role;
};

/**
 * @description Check if a user is a site admin and get their admin institution IDs.
 * - Site admins have admin role in one or more institutions but are not org admins.
 * - For now, District Admin user roles are not considered for Site Admin access to resources.
 * @todo We may want to consider "filter" callback as a second argument to this function
 */
export function getSiteAdminInfo(user: MagicSchoolUser | null | undefined) {
  if (!user || !user.user_role) {
    return { isOrgAdmin: false, isSiteAdmin: false, institutionIds: [] };
  }
  const isOrgAdmin = isAtLeastRole(user.user_role, 'org_admin');
  if (isOrgAdmin) {
    return { isOrgAdmin: true, isSiteAdmin: false, institutionIds: [] };
  }
  const institutionIds = user.institutionRoles
    .filter(({ role, institution }) => role === 'admin' && institution?.type !== 'district')
    .map(({ institution_id }) => institution_id);
  return { isOrgAdmin: false, isSiteAdmin: institutionIds.length > 0, institutionIds };
}

/**
 * Maps legacy user roles to institution roles
 */
export function getUserRoleForInstitutionRole(institutionRole: InstitutionRole): 'org_admin' | 'teacher' {
  switch (institutionRole) {
    case 'admin':
      return 'org_admin';
    case 'teacher':
      return 'teacher';
    default:
      throw new Error(`Unsupported institution role: ${institutionRole}`);
  }
}
